/**
 * Admin — choix du modèle de page d'accueil servi au public.
 *
 * La sélection est appliquée immédiatement (la landing lit GET /home-variant).
 * Chaque changement est audité côté serveur.
 */
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { getAdminHomeVariant, setAdminHomeVariant } from "@/api/admin";

export default function AdminHomeVariantPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "home-variant"],
    queryFn: getAdminHomeVariant,
  });

  const mut = useMutation({
    mutationFn: (id: string) => setAdminHomeVariant(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "home-variant"] }),
  });

  return (
    <Layout>
      <div className="mx-auto max-w-2xl px-6 py-12">
        <Link to="/admin" className="text-sm text-blue-900 underline">
          ← Admin
        </Link>
        <h1 className="mt-3 text-2xl font-bold">Page d'accueil</h1>
        <p className="mt-1 text-sm text-gray-600">
          Choisissez le modèle de page d'accueil servi aux visiteurs. Le changement
          est immédiat, sans redéploiement.
        </p>

        {isLoading && <p className="mt-6 text-sm text-gray-500">Chargement…</p>}
        {error && (
          <p className="mt-6 text-sm text-red-700">Impossible de charger les modèles.</p>
        )}

        {data && (
          <div className="mt-6 space-y-3">
            {data.variants.map((v) => {
              const active = v.id === data.current;
              return (
                <button
                  key={v.id}
                  type="button"
                  disabled={mut.isPending || active}
                  onClick={() => mut.mutate(v.id)}
                  aria-pressed={active}
                  className={`flex w-full items-start gap-3 rounded-lg border-2 bg-white p-4 text-left transition ${
                    active
                      ? "border-amber-500"
                      : "border-gray-200 hover:border-blue-700 hover:shadow"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 h-5 w-5 flex-none rounded-full border-2 ${
                      active ? "border-amber-500 bg-amber-500" : "border-gray-300"
                    }`}
                  />
                  <span>
                    <span className="flex items-center gap-2 font-medium text-gray-800">
                      {v.label}
                      {active && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          Actif
                        </span>
                      )}
                    </span>
                    <span className="mt-1 block text-sm text-gray-500">{v.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        <p className="mt-6 text-xs text-gray-400">
          Astuce : ouvrez la page d'accueil dans un autre onglet pour comparer les
          modèles avant de publier.
        </p>
      </div>
    </Layout>
  );
}
